<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { getTutorAbsentMsgNew } from "@/api/teachinfo";
import { absentNoticeOperate } from "@/api/layout";
import { uploader } from "@/utils/patchUpload.js";
import { _debounce } from "@/utils/tools";
import { useMessageStore } from "@/stores/message";
import { useConfirm } from "@/composables/useFeedback";

const messageStore = useMessageStore();
const { alert } = useConfirm();

const state = reactive({
  dialog: false,
  classTime: "",
  id: "",
  count: 0,
});

const cancelDialog = ref(false);
const IsICan = ref(false);
const formModel = reactive({
  reasonValue: null,
  cancleRemark: "",
  fileName: "",
});
const attachmentFile = ref(null);
const fileInputRef = ref(null);
const formRef = ref(null);
const submitting = ref(false);

const reasons = [
  { value: 1, label: "LAG" },
  { value: 2, label: "Internet Issue" },
  { value: 3, label: "Brownout" },
  { value: 4, label: "Family Reason" },
  { value: 5, label: "Equipment Problem (computer,headset,etc...)" },
  { value: 6, label: "Personal Reason" },
];

const penaltyRows = [
  { one: "CLASS TIME<3hrs", two: "3 times lesson fee", three: 2 },
  { one: "24hrs>CLASS TIME>3hrs", two: "100% lesson fee", three: 2 },
  { one: "48hrs>CLASS TIME>24hrs", two: "50% lesson fee", three: 2 },
  { one: "72hrs>CLASS TIME>48hrs", two: "25% lesson fee", three: 2 },
];

const mainVisible = computed({
  get: () => !!state.dialog,
  set: (v) => {
    state.dialog = v;
  },
});

function applyItem(item) {
  if (!item) {
    state.dialog = false;
    return;
  }
  state.dialog = true;
  state.classTime = item.ClassTimeStr || item.classTime || "";
  state.id = item.Id || item.id || "";
  state.count = item.Count || item.count || 1;
}

async function loadAbsent() {
  try {
    const res = await getTutorAbsentMsgNew();
    const list = res?.data?.Body;
    if (res?.data?.ErrorCode === 0 && Array.isArray(list) && list.length > 0) {
      applyItem(list[0]);
      messageStore.addAbsentMsg({
        classTime: list[0].ClassTimeStr,
        id: list[0].Id,
        count: list[0].Count,
      });
    }
  } catch (_) {
    /* ignore */
  }
}

function isCanAttendance(can) {
  IsICan.value = can;
  if (can) {
    submit();
  } else {
    cancelDialog.value = true;
  }
}

function openAttachmentFile() {
  fileInputRef.value?.click();
}

function uploadAttachmentFile(event) {
  const file = event.target.files?.[0];
  attachmentFile.value = file || null;
  formModel.fileName = file?.name || "";
}

function resetCancelForm() {
  formModel.reasonValue = null;
  formModel.cancleRemark = "";
  formModel.fileName = "";
  attachmentFile.value = null;
  if (fileInputRef.value) fileInputRef.value.value = "";
  formRef.value?.clearValidate?.();
}

watch(cancelDialog, (v) => {
  if (!v) resetCancelForm();
});

const submit = _debounce(async () => {
  if (submitting.value) return;

  let uploadBody = "";
  if (!IsICan.value) {
    const valid = await formRef.value?.validate?.().catch(() => false);
    if (!valid) return;
    if (!attachmentFile.value) {
      await alert("Required.", { type: "error" });
      return;
    }
    try {
      const _uploader = new uploader({ module: "Attachment" });
      const uploadResult = await _uploader.upload(attachmentFile.value);
      if (uploadResult.ErrorCode != 0) {
        await alert(uploadResult.Msg || "Upload fail.", { type: "error" });
        return;
      }
      uploadBody = uploadResult.Body;
    } catch (_) {
      await alert("Upload fail.", { type: "error" });
      return;
    }
  }

  submitting.value = true;
  try {
    const res = await absentNoticeOperate({
      Id: state.id,
      IsICan: IsICan.value,
      Attachment: IsICan.value ? "" : uploadBody,
      CancelReason: formModel.reasonValue,
      CancelRemark: formModel.cancleRemark,
    });
    messageStore.closeAbsentMsg();
    if (res?.data?.ErrorCode === 0 && res.data.Body === 1) {
      cancelDialog.value = false;
      state.dialog = false;
      // 若还有下一条，重新拉取
      await loadAbsent();
    } else {
      await alert(res?.data?.Msg || "Failed", { type: "error", icon: { default: true } });
    }
  } catch (_) {
    await alert("Failed", { type: "error" });
  } finally {
    submitting.value = false;
  }
});

onMounted(() => {
  loadAbsent();
});
</script>

<template>
  <el-dialog
    v-model="mainVisible"
    width="875px"
    align-center
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    class="course-attendance-dialog"
  >
    <div class="att-head">
      <h3 class="att-title">Confirmation of Course attendance</h3>
      <span class="att-count">1 / {{ state.count }}</span>
    </div>
    <div class="att-body">
      Dear Teacher :<br />
      &emsp;&emsp;Please confirm if you can attend the class on
      <strong>{{ state.classTime }}</strong><br />
      If there is no choice from you before 30 min of the class or you choose "No", the class will be
      cancelled automatically and you will get the penalty according to the rule.<br />
      &emsp;&emsp;Please note that there will be 3 times penalty for each class if you do not click it
      before 30 minutes.The deduction rule in details is below:<br />

      <el-table :data="penaltyRows" class="att-table" border style="margin-top: 24px">
        <el-table-column prop="one" label="Cancellation time" />
        <el-table-column prop="two" label="Penalty for each class" />
        <el-table-column prop="three" label="Teaching point deduction" width="180" />
      </el-table>
    </div>
    <div class="att-actions">
      <button type="button" class="tutor-blue-btn" :disabled="submitting" @click="isCanAttendance(true)">
        Yes, I can
      </button>
      <button type="button" class="tutor-red-btn" :disabled="submitting" @click="isCanAttendance(false)">
        No, I can't
      </button>
    </div>

    <el-dialog
      v-model="cancelDialog"
      width="700px"
      align-center
      append-to-body
      :close-on-click-modal="false"
      title="Cancel class"
    >
      <el-form ref="formRef" label-width="180px" :model="formModel">
        <el-form-item label="Class Time:" required>
          <el-input :model-value="state.classTime" disabled />
        </el-form-item>
        <el-form-item
          label="Cancel the reason:"
          prop="reasonValue"
          :rules="[{ required: true, message: 'Required.', trigger: 'change' }]"
        >
          <el-select v-model="formModel.reasonValue" placeholder="Select" style="width: 100%">
            <el-option
              v-for="r in reasons"
              :key="r.value"
              :label="r.label"
              :value="r.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          label="Note:"
          prop="cancleRemark"
          :rules="[{ required: true, message: 'Required.', trigger: 'blur' }]"
        >
          <el-input v-model="formModel.cancleRemark" type="textarea" :rows="3" />
        </el-form-item>
        <el-form-item
          label="The attachment upload:"
          prop="fileName"
          :rules="[{ required: true, message: 'Required.', trigger: 'change' }]"
        >
          <div class="upload-row">
            <el-input v-model="formModel.fileName" disabled />
            <input
              ref="fileInputRef"
              type="file"
              accept=".jpg,.png"
              class="hidden-file"
              @change="uploadAttachmentFile"
            />
            <button type="button" class="select-file-btn" @click="openAttachmentFile">
              Select the file
            </button>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="att-actions">
          <button type="button" class="tutor-blue-btn" :disabled="submitting" @click="submit">
            Submit
          </button>
        </div>
      </template>
    </el-dialog>
  </el-dialog>
</template>

<style scoped lang="scss">
.att-head {
  position: relative;
  text-align: center;
  margin-bottom: 8px;
}

.att-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #32397a;
}

.att-count {
  position: absolute;
  right: 0;
  top: 0;
  color: #5b6195;
  font-size: 13px;
}

.att-body {
  color: #32397a;
  line-height: 1.6;
  font-size: 14px;
}

.att-table {
  width: 100%;
}

.att-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
}

.upload-row {
  display: flex;
  gap: 8px;
  width: 100%;
  align-items: center;
}

.hidden-file {
  display: none;
}

.select-file-btn {
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 4px;
  background: #757575;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
}
</style>

<style lang="scss">
.course-attendance-dialog .el-dialog__header {
  display: none;
}

.course-attendance-dialog .el-dialog__body {
  padding: 20px 24px 24px;
  color: #32397a;
}
</style>
